<script setup lang="ts">
import type { RouteLocationRaw } from 'vue-router';
import { viewPathFor } from '@/navigation/areas';
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { toast } from 'vue-sonner';
import { usePersistentTableStore } from '@/stores/persistentTableStore';
import { useEntityStore } from '@/stores/entityStore';
import {
  isPageLevelEntityId,
  pageLevelEntityLabel,
  pageLevelEntityRouteName,
} from '@/composables/pageLevelTableEntities';
import {
  countRulesInFilter,
  countVisibleColumns,
  LEGACY_SHARE_PAYLOAD_PARAM,
  SHARE_KEY_PARAM,
} from '@/utils/savedViewSharing';
import {
  expandCardFields,
  expandDefinition,
  expandGroupBy,
  isSupportedDefinition,
} from '@/utils/savedViewDefinition';
import type { SavedViewDefinitionV2 } from '@/utils/savedViewDefinition';
import {
  useSharedSavedViewByKeyQuery,
  useImportSharedSavedView,
} from '@/composables/api/useSharedSavedViewsApi';
import * as Sentry from '@sentry/vue';

/**
 * Landing page for shared-view links: `/views/import?s=<shareKey>`.
 *
 * Resolves the key against the server, shows a confirmation dialog with the
 * view name + sharer + a brief contents summary, and on Import creates the
 * recipient's own independent copy before navigating to the destination page.
 * Cancel bounces back to the dashboard.
 *
 * The confirmation is deliberate: a recipient should know which view is about
 * to replace their filter and column layout for that scope.
 *
 * TWO THINGS THAT LOOK LIKE BUGS AND ARE NOT:
 *
 * 1. A key for an entity the recipient cannot view returns the SAME 404 as a
 *    key that was never issued, so this page cannot tell them apart and does
 *    not try. Distinguishing them would confirm the row exists — and the view's
 *    name is exactly what the permission filter exists to withhold. The message
 *    therefore covers both.
 * 2. Legacy `?d=` links are detected but never DECODED. Their payload is in the
 *    URL, so decoding it to show a helpful name would hand the reader the view
 *    name and every filter value with no permission check — reinstating the one
 *    leak dropping `?d=` closed.
 */

const route = useRoute();
const router = useRouter();
const persistentTableStore = usePersistentTableStore();
const entityStore = useEntityStore();

const shareKey = computed(() => {
  const raw = route.query[SHARE_KEY_PARAM];
  return typeof raw === 'string' && raw !== '' ? raw : null;
});

/**
 * LATCHED, not computed off the route.
 *
 * We strip the payload out of the address bar the moment we see one — it is a
 * whole view, name and every filter value, sitting in the URL, and leaving it
 * there keeps it in browser history, in any referrer this page sends, and in
 * whatever the user pastes next. We never decode it; we should not carry it
 * either. But that strip clears the query, so a computed would immediately read
 * false and the page would show "missing share key" instead of telling the user
 * their link is retired.
 */
const isLegacyLink = ref(typeof route.query[LEGACY_SHARE_PAYLOAD_PARAM] === 'string'
  && route.query[LEGACY_SHARE_PAYLOAD_PARAM] !== '');

if (isLegacyLink.value) {
  router.replace({ query: {} });
}

const sharedViewQuery = useSharedSavedViewByKeyQuery(shareKey);
const importMutation = useImportSharedSavedView();

const sharedView = computed(() => sharedViewQuery.data.value ?? null);

/**
 * True when the resolver failed for a reason that is not "no such key".
 *
 * Keyed off isError, not off the status alone. An axios NETWORK error — offline,
 * timed out, DNS, CORS — carries no `response` at all, so reading the status gave
 * `undefined` and this returned false: a train going into a tunnel produced "this
 * link is invalid, check with whoever sent it", which sends the user to badger the
 * sender over something a retry would fix. `retry: false` on the query means one
 * dropped request is enough to land here.
 */
const resolveFailedWithServerError = computed(() => {
  if (!sharedViewQuery.isError.value) {
    return false;
  }
  const status = (sharedViewQuery.error.value as { response?: { status?: number } } | null)
    ?.response?.status;
  return status !== 404;
});
const isResolving = computed(() => Boolean(shareKey.value) && sharedViewQuery.isLoading.value);

/**
 * The resolved definition, or null.
 *
 * `definition` is optional on the record because the library LISTING does not send
 * one — only the share-link resolver does, and this page always works from a
 * resolved link. Narrowed once here so the summary below reads it without each
 * caller re-deciding what an absent definition means.
 */
const resolvedDefinition = computed<SavedViewDefinitionV2 | null>(() =>
  isSupportedDefinition(sharedView.value?.definition) ? sharedView.value.definition : null,
);

/** A resolved view whose definition this client cannot read. See linkError. */
const isUnrenderable = computed(() => sharedView.value !== null && resolvedDefinition.value === null);

/**
 * The single failure message. One string for "no such key", "deleted", and
 * "you cannot see this entity" — see note 1 at the top of this file.
 */
const linkError = computed<string | null>(() => {
  if (isLegacyLink.value) {
    return 'This is an older share link and is no longer supported. Ask whoever sent it to share the view again.';
  }
  if (!shareKey.value) {
    return 'This link is missing its share key.';
  }
  // A 404 is the deliberate both-causes case. Anything else — a 500, a dropped
  // connection — is OUR failure, and telling the user their link is invalid sends
  // them to badger the sender over something that would work on a retry.
  if (resolveFailedWithServerError.value) {
    return 'Could not open this link right now. Try again in a moment.';
  }
  if (sharedViewQuery.isError.value || (!isResolving.value && sharedView.value === null)) {
    return "This link is either invalid or points at data you don't have access to. Check with whoever sent it.";
  }
  // Checked BEFORE the summary renders, not after the import POST returns.
  // `SavedViewDefinition` accepts any version from 1 to 255 and never requires a
  // `filter` key, so `{"v":2,"columns":[]}` is a publishable definition — and
  // countRulesInFilter walking `undefined.ruleScopes` threw inside a computed
  // during render, which blanks the page instead of showing this branch.
  if (isUnrenderable.value) {
    return 'This shared view was saved by a newer version of the app and cannot be opened here.';
  }
  return null;
});

const ruleCount = computed(() =>
  resolvedDefinition.value ? countRulesInFilter(resolvedDefinition.value.filter) : 0,
);
const visibleColumnCount = computed(() =>
  resolvedDefinition.value ? countVisibleColumns(resolvedDefinition.value) : 0,
);

const entityLabel = computed(() => {
  const id = sharedView.value?.entity;
  if (!id) return '';
  // For search-page entities, surface the entity's display name from the entity
  // store if we have one — otherwise just show the raw id.
  const entity = entityStore.entities.find((candidate) => candidate.entityId === id);
  return pageLevelEntityLabel(id) ?? entity?.entityDisplayName ?? id;
});

/**
 * Where an imported view opens, with the view in the address: the entity's own
 * list, for example `/players?view=12`, or a page's view on its page, for
 * example `/track-matching?view=31`. A scope with no list in an area but a
 * route of its own opens that page. Anything else falls back to Home.
 */
function destinationRouteFor(entityId: string, viewId: number): RouteLocationRaw {
  const viewPath = viewPathFor(entityId, viewId);
  if (viewPath) {
    return viewPath;
  }
  const pageRouteName = pageLevelEntityRouteName(entityId);
  return pageRouteName ? { name: pageRouteName } : { name: 'home' };
}

async function onImport() {
  const shared = sharedView.value;
  const key = shareKey.value;
  if (!shared || !key) {
    return;
  }

  // The server refuses an entity the caller cannot view, so this never creates
  // a row the recipient could not reach. The mutation toasts its own failure.
  let imported;
  try {
    imported = await importMutation.mutateAsync({ entity: shared.entity, shareKey: key });
  } catch {
    return;
  }

  if (!isSupportedDefinition(imported.definition)) {
    // Saved on the server, but this client cannot render it. Say so instead of
    // navigating to a page that would apply half a layout.
    toast.error(`Imported "${imported.name}", but it needs a newer version of the app to open.`);
    router.replace({ name: 'home' });
    return;
  }

  // CAN THE PICKER EVEN SHOW THIS? The server refuses an entity the recipient
  // cannot VIEW, and it is tempting to stop there — but the picker answers a
  // different question. It lists models.json minus a frontend-only hidden set,
  // and entityStore says of that filter, in its own words, "UX, not security".
  //
  // So a permitted-but-hidden entity got this far, setSelectedEntity accepted
  // it, the store's self-heal watcher quietly rewrote it to entities[0], and the
  // user landed on an unrelated page under a green "Imported view" toast with no
  // explanation. The row really was created, so say that rather than implying
  // nothing happened.
  const destinationIsReachable = isPageLevelEntityId(imported.entity)
    || entityStore.entities.some((entity) => entity.entityId === imported.entity);

  if (!destinationIsReachable) {
    toast.error(
      `Imported "${imported.name}", but ${imported.entity} views cannot be opened from here.`,
    );
    router.replace({ name: 'home' });

    return;
  }

  // Apply it to the destination scope so the page shows the view on arrival.
  // Everything goes THROUGH applyDefinitionToScope, the active-view id included:
  // we are routed to /views/import, so the store's own computed setters write to
  // whichever entity the picker currently holds, not to the view's scope.
  //
  // WRAPPED, because the row already exists by the time we get here. develop had
  // a try/catch and the new call chain lost it, while gaining two functions that
  // can throw on data the server accepts. Without this the throw escaped the
  // handler entirely: no toast, no navigation, and isPending flipped back so the
  // button read "Import" again — press it again and you own two copies, still
  // having been told nothing.
  try {
    persistentTableStore.applyDefinitionToScope(
      imported.entity,
      imported.definition.filter,
      expandDefinition(imported.entity, imported.definition),
      imported.definition.layout,
      imported.id,
      expandCardFields(imported.entity, imported.definition),
      expandGroupBy(imported.entity, imported.definition),
    );
  } catch (error) {
    // Not swallowed — this is a definition the gate above accepted and the
    // expansion could not use, which is a bug we want to hear about.
    Sentry.captureException(error);
    toast.error(
      `Imported "${imported.name}", but it could not be opened. It is saved to your views.`,
    );
    router.replace({ name: 'home' });

    return;
  }

  // Search-page entities render whichever entity the entity store has selected,
  // so it has to be set before navigating. The page-level scopes derive theirs
  // from the route and must not be touched.
  if (!isPageLevelEntityId(imported.entity)) {
    entityStore.setSelectedEntity(imported.entity);
  }

  toast.success(`Imported view "${imported.name}".`);
  router.replace(destinationRouteFor(imported.entity, imported.id));
}

function onCancel() {
  router.replace({ name: 'home' });
}
</script>

<template>
  <!-- We use a controlled Dialog with a constant `open` so the modal is the
       page's entire UI — there's no underlying content the user could
       interact with while the modal is up. -->
  <Dialog :open="true">
    <DialogContent class="max-w-md">
      <!-- data-testid lives on a real DOM node here: DialogContent renders a DialogPortal
           (teleport/fragment root), so a fallthrough attr on it is dropped. `contents` keeps
           the wrapper layout-neutral. -->
      <div data-testid="import-saved-view-dialog" class="contents">
      <template v-if="linkError">
        <DialogHeader>
          <DialogTitle>
            {{ isLegacyLink ? 'This share link is out of date' : 'This share link looks broken' }}
          </DialogTitle>
          <DialogDescription data-testid="import-saved-view-error">
            {{ linkError }}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" @click="onCancel">Back to dashboard</Button>
        </DialogFooter>
      </template>
      <template v-else-if="sharedView">
        <DialogHeader>
          <DialogTitle data-testid="import-saved-view-title">
            Import view "{{ sharedView.name }}"?
          </DialogTitle>
          <DialogDescription>
            <span v-if="sharedView.shared_by_name">
              Shared by <span class="font-medium">{{ sharedView.shared_by_name }}</span>.<br />
            </span>
            <span class="text-muted-foreground text-xs">
              Adds to your <span class="font-medium">{{ entityLabel }}</span>
              saved views — {{ ruleCount }} filter rule{{ ruleCount === 1 ? '' : 's' }},
              {{ visibleColumnCount }} visible column{{ visibleColumnCount === 1 ? '' : 's' }}.
              Loading this view will replace the filter and column layout you
              currently have on that page.
            </span>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="ghost" data-testid="import-saved-view-cancel" @click="onCancel">
            Cancel
          </Button>
          <Button
            data-testid="import-saved-view-confirm"
            :disabled="importMutation.isPending.value"
            @click="onImport"
          >
            {{ importMutation.isPending.value ? 'Importing…' : 'Import' }}
          </Button>
        </DialogFooter>
      </template>
      <template v-else>
        <DialogHeader>
          <DialogTitle>Loading share link&hellip;</DialogTitle>
          <DialogDescription class="sr-only">Checking which saved view this link shares.</DialogDescription>
        </DialogHeader>
      </template>
      </div>
    </DialogContent>
  </Dialog>
</template>
